/** Android application id rules: ≥ 2 dot-separated segments, each starting with a letter. */
const PACKAGE_RE = /^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z][a-zA-Z0-9_]*)+$/;

export function isValidPackageName(value: string): boolean {
  return value.length <= 191 && PACKAGE_RE.test(value);
}

/**
 * Extract the package name from a Google Play link or accept a bare package
 * name. Returns null for anything else (other hosts, missing id, …).
 *
 *   https://play.google.com/store/apps/details?id=com.whatsapp&hl=en → com.whatsapp
 *   play.google.com/store/apps/details?id=com.whatsapp                → com.whatsapp
 *   market://details?id=com.whatsapp                                  → com.whatsapp
 *   com.whatsapp                                                      → com.whatsapp
 */
export function parsePlayStorePackage(input: string): string | null {
  const value = input.trim();
  if (!value) return null;
  if (isValidPackageName(value)) return value;

  let url: URL;
  try {
    url = new URL(/^[a-z]+:\/\//i.test(value) ? value : `https://${value}`);
  } catch {
    return null;
  }
  const isPlay =
    (url.protocol === 'https:' || url.protocol === 'http:') &&
    url.hostname === 'play.google.com' &&
    url.pathname.replace(/\/+$/, '') === '/store/apps/details';
  const isMarket = url.protocol === 'market:' && url.hostname === 'details';
  if (!isPlay && !isMarket) return null;

  const id = url.searchParams.get('id');
  return id && isValidPackageName(id) ? id : null;
}

export function playStoreUrlFor(packageName: string): string {
  return `https://play.google.com/store/apps/details?id=${encodeURIComponent(packageName)}`;
}

/** Google Play icon URLs accept a size suffix (=s96); other URLs are returned unchanged. */
export function sizedIconUrl(iconUrl: string, size = 96): string {
  try {
    const url = new URL(iconUrl);
    if (url.hostname !== 'play-lh.googleusercontent.com') return iconUrl;
    return `${iconUrl.replace(/=[^/]*$/, '')}=s${size}`;
  } catch {
    return iconUrl;
  }
}

/** Metadata returned by the Play Store lookup endpoint. */
export interface PlayStoreAppInfo {
  packageName: string;
  playStoreUrl: string;
  name: string;
  developerName: string | null;
  iconUrl: string | null;
  storeCategory: string | null;
  /** Developer contact email shown under "App support". */
  developerEmail: string | null;
  /** First release date (YYYY-MM-DD, as shown on Play). */
  releasedOn: string | null;
  /** "Updated on" date (YYYY-MM-DD). */
  storeUpdatedOn: string | null;
  /** "About the developer" on the listing. */
  developerLegalName: string | null;
  /** Email under "About the developer" (may differ from the support email). */
  developerContactEmail: string | null;
  developerAddress: string | null;
  developerPhone: string | null;
  developerWebsite: string | null;
  privacyPolicyUrl: string | null;
}
